import { useState, useEffect } from "react";
import "./QuestionPicker.css";
import Modal from "../shared/Modal";
import { CreateQuestionForFormPayload, Question, QuestionType, UpdateFormQuestionOrderPayload } from "effno-core";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { selectEditingForm, setEditingForm, setEditingQuestionId, setUIStates } from "../../redux/features/appData";
import { apiCreateQuestionForForm } from "../../services/QuestionService";
import { useDispatch } from "react-redux";
import { isEmptyJSON } from "../../utils/utils";
import { 
    EMAIL_SETTINGS,
    FILE_UPLOAD_SETTINGS,
    LONG_SETTINGS,
    MULTIPLE_SETTINGS,
    SHORT_SETTINGS,
    WEBSITE_SETTINGS,
    END_SETTINGS,
    PHONE_SETTINGS,
    // TIME_SETTINGS,
    CONTACT_SETTINGS,
    NUMBER_SETTINGS,
    NPS_SETTINGS,
    SCORE_SETTINGS,
    RATING_SETTINGS,
    DATE_SETTINGS,
    SECTION_SETTINGS,
    QUESTION_TITLES,
} from "../config/questionSettings";
import { apiFormHasEdits, apiUpdateFormQuestionOrder } from "../../services/FormService";
import { addNewQuestionToOrder } from "../shared/uiHelpers";
import { FormQuestionTag, QuestionTypeIconColors } from "../form-question/FormQuestionTag";

type QuestionPickerProps = {
    isOpen: boolean;
    onClose: () => void;
}

export const QuestionPicker = ({isOpen, onClose}: QuestionPickerProps) => {

    const dispatch = useDispatch();

    const editingForm = useSelector(selectEditingForm);

    const handlePickQuestion = async (type: QuestionType) => {
        console.log(type);
        
        const newQuestion : CreateQuestionForFormPayload = {
            formId: editingForm?.id || "",
            type,
            title: QUESTION_TITLES[type],
            description: type === QuestionType.END ? "Description (optional)" : "",
            conditions: "{}",
            settings: "{}",
            required: false
        }

        switch(type) {
            case QuestionType.SHORT:
                // move all this to a constant object indexed by type in another file
                newQuestion.settings = JSON.stringify(SHORT_SETTINGS)
                break;
            case QuestionType.LONG:
                newQuestion.settings = JSON.stringify(LONG_SETTINGS);
                break;
            case QuestionType.EMAIL:
                newQuestion.settings = JSON.stringify(EMAIL_SETTINGS);
                break;
            case QuestionType.WEBSITE:
                newQuestion.settings = JSON.stringify(WEBSITE_SETTINGS);
                break;
            case QuestionType.MULTIPLE:
                newQuestion.settings = JSON.stringify(MULTIPLE_SETTINGS);                
                break;
            case QuestionType.DATE:
                newQuestion.settings = JSON.stringify(DATE_SETTINGS);                
                break;
            case QuestionType.FILE_UPLOAD:
                newQuestion.settings = JSON.stringify(FILE_UPLOAD_SETTINGS);                
                break;
            case QuestionType.NUMBER:
                newQuestion.settings = JSON.stringify(NUMBER_SETTINGS);                
                break;
            case QuestionType.PHONE:
                newQuestion.settings = JSON.stringify(PHONE_SETTINGS);                
                break;
            // case QuestionType.TIME:
            //     newQuestion.settings = JSON.stringify(TIME_SETTINGS);                
            //     break;
            case QuestionType.CONTACT:
                newQuestion.settings = JSON.stringify(CONTACT_SETTINGS);
                break;
            case QuestionType.NPS:
                newQuestion.settings = JSON.stringify(NPS_SETTINGS);
                break;
            case QuestionType.SCORE:
                newQuestion.settings = JSON.stringify(SCORE_SETTINGS);
                break;
            case QuestionType.RATING:
                newQuestion.settings = JSON.stringify(RATING_SETTINGS);
                break;
            case QuestionType.SECTION:
                newQuestion.settings = JSON.stringify(SECTION_SETTINGS);
                break;
            case QuestionType.END:
                newQuestion.settings = JSON.stringify(END_SETTINGS);                
                break;
            default:
                toast("Invalid Question Type", toastFail);
                return
        }

        const question = await apiCreateQuestionForForm(newQuestion);
        if (!question) {
            toast("Failed to create question", toastFail);
            return;
        }

        await apiFormHasEdits(editingForm?.id || "");
        
        if (editingForm) {
            const allQuestions : Question[] = editingForm.questions ? [...editingForm.questions] : [];
            allQuestions.push(question);

            const newOrder = addNewQuestionToOrder(editingForm.questions, question, editingForm?.questionOrder);

            dispatch(setEditingForm({
                ...editingForm, 
                hasEdits: true,
                questions: allQuestions,
                questionOrder: newOrder
            }));

            const payload: UpdateFormQuestionOrderPayload = {
                formId: editingForm.id,
                questionOrder: newOrder
            }

            const updatedForm = await apiUpdateFormQuestionOrder(payload);

            if (!updatedForm) {
                toast("Failed to update form question order", toastFail);
                return;
            }

            dispatch(setEditingQuestionId(question.id));
            // toast("Question created: " + type, toastSuccess);
            console.log("[QuestionPicker/handlePickQuestion] allQuestions: ", allQuestions);
        }

        onClose();
    }

    return (
        <Modal
            isOpen={isOpen}
            style={null}
            onClose={onClose}
            canClose={true}
            allowClickout={true}
            // escapeToClose={false}
        >
            <div 
                className="question-picker-container"
                data-testid="question-picker"
            >
                <div className="question-picker-header">
                    Select a Question type
                </div>

                <div className="question-picker-columns">                
                    <div className="question-picker-column">
                        <div className="question-picker-title">
                            Recommended
                        </div>
                        <div 
                            className="question-picker-recommended"
                            style={{backgroundColor: QuestionTypeIconColors[QuestionType.SHORT]}}
                            data-testid={"recommended-" + QuestionType.SHORT}
                            onClick={()=>handlePickQuestion(QuestionType.SHORT)}
                        >
                            <FormQuestionTag type={QuestionType.SHORT} /> Short Text
                        </div>

                        <div 
                            className="question-picker-recommended"
                            style={{backgroundColor: QuestionTypeIconColors[QuestionType.CONTACT]}}
                            data-testid={"recommended-" + QuestionType.CONTACT}
                            onClick={()=>handlePickQuestion(QuestionType.CONTACT)}
                        >
                            <FormQuestionTag type={QuestionType.CONTACT} /> Contact
                        </div>

                        <div 
                            className="question-picker-recommended"
                            style={{backgroundColor: QuestionTypeIconColors[QuestionType.MULTIPLE]}}
                            data-testid={"recommended-" + QuestionType.MULTIPLE}
                            onClick={()=>handlePickQuestion(QuestionType.MULTIPLE)}
                        >
                            <FormQuestionTag type={QuestionType.MULTIPLE} /> Multiple
                        </div>

                        <div 
                            className="question-picker-recommended"
                            style={{backgroundColor: QuestionTypeIconColors[QuestionType.EMAIL]}}
                            data-testid={"recommended-" + QuestionType.EMAIL}
                            onClick={()=>handlePickQuestion(QuestionType.EMAIL)}
                        >
                            <FormQuestionTag type={QuestionType.EMAIL} /> Email
                        </div>

                        <div 
                            className="question-picker-recommended"
                            style={{backgroundColor: QuestionTypeIconColors[QuestionType.NPS]}}
                            data-testid={"recommended-" + QuestionType.NPS}
                            onClick={()=>handlePickQuestion(QuestionType.NPS)}
                        >
                            <FormQuestionTag type={QuestionType.NPS} /> NPS
                        </div>

                        <div 
                            className="question-picker-recommended"
                            style={{backgroundColor: QuestionTypeIconColors[QuestionType.END]}}
                            data-testid={"recommended-" + QuestionType.END}
                            onClick={()=>handlePickQuestion(QuestionType.END)}
                        >
                            <FormQuestionTag type={QuestionType.END}/> End
                        </div>
                    </div>
                    
                    <div className="question-picker-column">
                        <div className="question-picker-title">
                            Common 
                        </div>
                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.SHORT}
                            onClick={()=>handlePickQuestion(QuestionType.SHORT)}
                        >
                            <FormQuestionTag type={QuestionType.SHORT}/> Short Text
                        </div>
                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.LONG}
                            onClick={()=>handlePickQuestion(QuestionType.LONG)}
                        >
                            <FormQuestionTag type={QuestionType.LONG}/>Long Text
                        </div>
                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.MULTIPLE}
                            onClick={()=>handlePickQuestion(QuestionType.MULTIPLE)}
                        >               
                            <FormQuestionTag type={QuestionType.MULTIPLE}/> Multiple Choice
                        </div>

                        <div className="question-picker-spacer">
                            {/* Spacer */}
                        </div>

                        <div className="question-picker-title">
                            Contact 
                        </div>

                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.CONTACT}
                            onClick={()=>handlePickQuestion(QuestionType.CONTACT)}
                        >
                            <FormQuestionTag type={QuestionType.CONTACT}/> Contact
                        </div>

                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.PHONE}
                            onClick={()=>handlePickQuestion(QuestionType.PHONE)}
                        >
                            <FormQuestionTag type={QuestionType.PHONE}/> Phone
                        </div>

                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.EMAIL}
                            onClick={()=>handlePickQuestion(QuestionType.EMAIL)}
                        >
                            <FormQuestionTag type={QuestionType.EMAIL}/> Email
                        </div>
                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.WEBSITE}
                            onClick={()=>handlePickQuestion(QuestionType.WEBSITE)}
                        >
                            <FormQuestionTag type={QuestionType.WEBSITE}/> Website
                        </div>
                    </div>

                    <div className="question-picker-column">

                        <div className="question-picker-title">
                            Opinion 
                        </div>

                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.NPS}
                            onClick={()=>handlePickQuestion(QuestionType.NPS)}
                        >
                            <FormQuestionTag type={QuestionType.NPS}/> NPS Score
                        </div>
                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.SCORE}
                            onClick={()=>handlePickQuestion(QuestionType.SCORE)}
                        >
                            <FormQuestionTag type={QuestionType.SCORE}/> Opinion Score
                        </div>
                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.RATING}
                            onClick={()=>handlePickQuestion(QuestionType.RATING)}
                        >
                            <FormQuestionTag type={QuestionType.RATING}/> Rating
                        </div>

                        <div className="question-picker-spacer">
                            {/* Spacer */}
                        </div>
                        <div className="question-picker-title">
                            Other 
                        </div>

                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.DATE}
                            onClick={()=>handlePickQuestion(QuestionType.DATE)}
                        >
                            <FormQuestionTag type={QuestionType.DATE}/> Date
                        </div>
                        {/* <div 
                            className="question-picker-question"
                            data-testid={QuestionType.FILE_UPLOAD}
                            onClick={()=>handlePickQuestion(QuestionType.FILE_UPLOAD)}
                        >
                            <FormQuestionTag type={QuestionType.FILE_UPLOAD}/> File Upload
                        </div> */}
                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.NUMBER}
                            onClick={()=>handlePickQuestion(QuestionType.NUMBER)}
                        >
                            <FormQuestionTag type={QuestionType.NUMBER}/> Number
                        </div>
                    
                        {/* <div 
                            className="question-picker-question"
                            data-testid={QuestionType.TIME}
                            onClick={()=>handlePickQuestion(QuestionType.TIME)}
                        >
                            <FormQuestionTag type={QuestionType.TIME}/> Time
                        </div> */}
                        
                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.SECTION}
                            onClick={()=>handlePickQuestion(QuestionType.SECTION)}
                        >
                            <FormQuestionTag type={QuestionType.SECTION}/> Section
                        </div>

                        <div 
                            className="question-picker-question"
                            data-testid={QuestionType.END}
                            onClick={()=>handlePickQuestion(QuestionType.END)}
                        >
                            <FormQuestionTag type={QuestionType.END}/> End
                        </div>

                    </div>
                </div>
            </div>
      </Modal>
    )
}